import moment from 'moment';
import React, { useCallback } from 'react';
import { PiEnvelopeSimple } from 'react-icons/pi';
import { useParams } from 'react-router-dom';
import { Input } from 'rizzui';
import PhoneInput from '../../../../../components/forms/PhoneInputWrapper';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import { ClientService } from '../../../../../services/client/clientService';
import { ClientApiResponse } from '../../../../../type/entities/client-type';
import ClientProfileLayout from '../../ClientProfileLayout';

interface ClientInfoProps { }

const ClientInfo: React.FC<ClientInfoProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const service = new ClientService();

    const [data, loading, , refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getClientById(id);
        return response.getResponseData() as ClientApiResponse;
    }, [id]));

    return (
        <ClientProfileLayout isLoading={loading} onCreditsUpdated={refetch}>
            <div className="@container">
                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                    <FormGroup
                        title="Información General"
                        description="Datos principales"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            label="CIF/NIF"
                            placeholder="12345678A"
                            value={data?.cifNif ?? ""}
                        />
                        <Input
                            readOnly
                            label="Razón Social"
                            placeholder="Nombre de la empresa"
                            value={data?.companyName ?? ""}
                        />
                        <Input
                            readOnly
                            prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                            label="Email"
                            placeholder="info@attiva.es"
                            value={data?.email ?? ""}
                            className="md:col-span-2"
                        />
                        <Input
                            readOnly
                            label="CNAE"
                            placeholder="1234"
                            value={data?.cnae ?? ""}
                        />
                        <Input
                            readOnly
                            label="Descripción de la actividad"
                            placeholder="Actividad de la empresa"
                            value={data?.activityDescription ?? ""}
                            className="md:col-span-2"
                        />
                        <Input
                            readOnly
                            label="IBAN"
                            placeholder="ES123456789123456789"
                            value={data?.iban ?? ""}
                        />
                        <PhoneInput
                            id="telephone"
                            readOnly
                            label="Teléfono"
                            placeholder="+34 123456789"
                            value={data?.telephone ?? ""}
                            required={false}
                        />
                        <PhoneInput
                            id="telephoneSecondary"
                            readOnly
                            label="Teléfono secundario"
                            placeholder="+34 123456789"
                            value={data?.telephoneSecondary ?? ""}
                            required={false}
                        />
                        <PhoneInput
                            id="mobile"
                            readOnly
                            label="Móvil"
                            placeholder="+34 123456789"
                            value={data?.mobile ?? ""}
                            required={false}
                        />
                        <Input
                            readOnly
                            label="Créditos"
                            placeholder=""
                            value={data?.currentCredits ?? ""}
                        />
                        <Input
                            readOnly
                            label="Asesoría"
                            placeholder="Nombre de la asesoría"
                            value={data?.consultancy?.name ?? ""}
                            className="md:col-span-2"
                        />
                        <Input
                            readOnly
                            label="Fecha de creación"
                            placeholder="Fecha de creación"
                            value={moment(data?.companyCreationDate?.date).format('DD/MM/YYYY') ?? ""}
                        />
                        <Input
                            readOnly
                            label="Número de la Seguridad Social"
                            placeholder="123456789"
                            value={data?.socialSecurityNumber ?? ""}
                        />
                        <Input
                            readOnly
                            label="Número de trabajadores"
                            placeholder="0"
                            value={data?.workersNumber ?? ""}
                        />
                        <Input
                            readOnly
                            label="Empresa con RLT"
                            value={data?.hasRlt === true ? 'Sí' : data?.hasRlt === false ? 'No' : ''}
                        />
                        <Input
                            readOnly
                            label="Código BIC"
                            placeholder=""
                            value={data?.bic ?? ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Domicilio social"
                        description="Datos de la dirección"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-6"
                    >
                        <Input
                            readOnly
                            label="Provincia"
                            placeholder="Provincia"
                            value={data?.registeredOfficeProvince ?? ""}
                        />
                        <Input
                            readOnly
                            label="Población"
                            placeholder="Población"
                            value={data?.registeredOfficeTown ?? ""}
                        />
                        <Input
                            readOnly
                            label="Dirección"
                            placeholder="Dirección de la empresa"
                            value={data?.registeredOfficeAddress ?? ""}
                            className='md:col-span-3'
                        />
                        <Input
                            readOnly
                            label="Código Postal"
                            placeholder="12345"
                            value={data?.registeredOfficePostalCode ?? ""}
                        />
                    </FormGroup>

                    <FormGroup
                        title="Responsable"
                        description="Datos del responsable"
                        className='pt-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-3"
                    >
                        <Input
                            readOnly
                            label="CIF/NIF"
                            placeholder="12345678A"
                            value={data?.personResponsibleNif ?? ""}
                        />
                        <Input
                            readOnly
                            label="Nombre y apellidos"
                            placeholder="Nombre del responsable"
                            value={data?.personResponsibleName ?? ""}
                        />
                        <Input
                            prefix={<PiEnvelopeSimple className="h-6 w-6 text-gray-500" />}
                            label="Email"
                            placeholder="info@attiva.es"
                            value={data?.personResponsibleEmail ?? ""}
                            readOnly
                        />
                        <PhoneInput
                            id="personResponsibleTelephone"
                            readOnly
                            label="Teléfono"
                            placeholder="+34 123456789"
                            value={data?.personResponsibleTelephone ?? ""}
                            required={false}
                        />
                        <PhoneInput
                            id='personResponsibleTelephoneSecondary'
                            readOnly
                            label='Teléfono Secundario'
                            placeholder='+34 123456789'
                            value={data?.personResponsibleTelephoneSecondary ?? ''}
                            required={false}
                        />
                    </FormGroup>
                </div>
            </div>
        </ClientProfileLayout>
    );
};

export default ClientInfo;
